<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";

const localePath = useLocalePath();

const {
  external = false,
  variant = "primary",
  size = "small",
  to,
} = defineProps<{
  external?: boolean;
  variant?: "primary" | "secondary";
  size?: "small" | "large";
  to?: RouteLocationRaw;
}>();

const component = computed(() =>
  to ? resolveComponent("NuxtLink") : "button",
);

const resolvedRoute = computed(() => {
  if (component.value === "button" || !to) {
    return undefined;
  }

  return localePath(to);
});
</script>

<template>
  <component
    :is="component"
    :external="to ? external : undefined"
    :to="resolvedRoute"
    :class="[
      'foundation-base-button',
      `foundation-base-button--${variant}`,
      `foundation-base-button--${size}`,
    ]"
    data-testid="foundation-base-button"
  >
    <span
      :class="[
        'foundation-base-button__label',
        `foundation-base-button__label--${variant}`,
        `foundation-base-button__label--${size}`,
      ]"
    >
      <slot />
    </span>
  </component>
</template>

<style lang="scss">
// Small primary is default combination
.foundation-base-button {
  $self: &;

  display: inline-block;
  color: var(--cm-color-white, #fff);
  font-weight: var(--cm-font-weight-bold, 700);
  text-decoration: none;
  background-color: var(--cm-color-primary, #007bff);
  border: 1px solid var(--cm-color-primary, #007bff);
  border-radius: var(--cm-border-radius, 0.25rem);
  transition: var(--cm-transition-fast, 200ms);
  padding-block: var(--spacing-2, 0.5rem);
  padding-inline: var(--spacing-6, 1.5rem);

  &--large {
    padding-block: var(--spacing-4, 1rem);
    padding-inline: var(--spacing-8, 2rem);
  }

  &__label {
    display: block;
    font-size: 1rem;
    line-height: 1.5;
    text-align: center;

    &--large {
      font-size: 1.125rem;
    }
  }

  &:disabled {
    color: var(--cm-color-neutral-500, #6c757d);
    background-color: var(--cm-color-neutral-50, #e0e0e0);
    border-color: var(--cm-color-neutral-50, #e0e0e0);
    cursor: not-allowed;
  }

  &--primary {
    @media (hover: hover) {
      &:hover {
        &:not(:disabled) {
          background-color: var(--cm-color-primary-darker, #0056b3);
          border-color: var(--cm-color-primary-darker, #0056b3);
        }
      }
    }

    &:active {
      &:not(:disabled) {
        background-color: var(--cm-color-primary-darkest, #004085);
        border-color: var(--cm-color-primary-darkest, #004085);
      }
    }
  }

  &--secondary {
    color: var(--cm-color-black, #007bff);
    background-color: var(--cm-color-white, #fff);
    border: 1px solid var(--cm-color-black, #000);

    @media (hover: hover) {
      &:hover {
        &:not(:disabled) {
          color: var(--cm-color-white, #fff);
          background-color: var(--cm-color-black, #000);
        }
      }
    }

    &:active {
      &:not(:disabled) {
        color: var(--cm-color-white, #fff);
        background-color: var(--cm-color-black, #000);
      }
    }
  }
}
</style>
